/** @jsxImportSource @emotion/react */
import React from 'react'
import { View } from '@components/atoms'
import { useNavigate } from 'react-router-dom'

import { mono } from './monolith.tokens'

export interface SpecRowType {
	label: string
	value: string
}

export interface DetailImageType {
	src: string
	alt: string
	/** 이미지 아래 13px 300 faint 한 줄 (DESIGN_SYSTEM §5 스크린샷 캡션) */
	caption?: string
}

/** 4:8 을 쓰지 않는 밴드 한 칸. 상단 헤더 + 전폭 본문 (DESIGN_SYSTEM §6.3) */
export interface DetailBandType {
	/** 좌측 키커. 사진만 있는 밴드에서는 사진 아래 캡션으로 내려간다 */
	kicker: string
	/** 앵커 id. 제품 서브 네비가 `#{id}` 로 착지한다 */
	id?: string
	/** 첫 문장. 없으면 목록만 */
	lede?: string
	/** 헤어라인 목록 — 좌 라벨 / 우 값 */
	rows?: SpecRowType[]
	/** 목록 뒤 한 문장 */
	note?: string
	/** 헤더 오른쪽의 텍스트 링크 한 개. 본문 끝에 두면 단어 하나가 홀로 뜬다 */
	link?: { label: string; to: string }
	/** 밴드 이미지. 배치는 아래 규칙이 내용에서 정한다 */
	images?: DetailImageType[]
	/**
	 * 이 이미지는 **읽어야 하는 그림**이라 폭이 필요하다 — 도표·채널 맵처럼 안에 글자가 있는 것.
	 * 기본(생략)은 텍스트 옆에 세우는 것이고, 이걸 켜면 전폭으로 눕는다.
	 */
	wideImage?: boolean
}

const hasText = (aBand: DetailBandType) =>
	aBand.lede !== undefined || aBand.rows !== undefined || aBand.note !== undefined

/** 글 + 사진 1장 → 글 7 : 사진 5 로 나란히 */
const isSplit = (aBand: DetailBandType) =>
	hasText(aBand) &&
	aBand.wideImage !== true &&
	aBand.images !== undefined &&
	aBand.images.length === 1

/** 사진만 있는 밴드 — 키커를 사진 위에 띄우지 않는다 (PERSONA §1: 설명하지 않는다) */
const isPlate = (aBand: DetailBandType) =>
	!hasText(aBand) &&
	aBand.images !== undefined &&
	aBand.images.length === 1 &&
	aBand.images[0].caption === undefined

/**
 * 상세 페이지의 본문 밴드 — `/products/{code}` 와 `/shop/{code}` 가 같이 쓴다.
 *
 * 배치는 **내용이 정한다**(DESIGN_SYSTEM §6.3). 스위치는 `wideImage` 하나뿐이다:
 *   글 + 사진 1장 → 글 7 : 사진 5,  `wideImage` → 전폭,
 *   사진만       → 판(plate),      여러 장 → 3열 그리드.
 *
 * 스타일은 `monoBandStyle` 을 페이지 css() 안에 스프레드해서 쓴다.
 */
export const MonolithBand = (props: { band: DetailBandType }) => {
	const navigate = useNavigate()
	const aBand = props.band

	const go = (to: string) => (e: React.MouseEvent) => {
		e.preventDefault()
		navigate(to)
	}

	const renderImages = (images: DetailImageType[]) => (
		<View className={`mnDetailFigures ${images.length > 1 ? 'mnDetailFigureGrid' : ''}`}>
			{images.map(anImage => (
				<figure key={anImage.src} className="mnDetailFigure">
					{/*
						지연 로딩은 **여러 장 묶음에만** 건다(설치 절차 17장). 밴드마다 한 장씩 있는
						이미지까지 지연시키면 자리를 안 잡고 있다가 나중에 높이를 밀어서, 서브 네비의
						섹션 앵커가 엉뚱한 곳에 착지한다.
					*/}
					<img
						src={anImage.src}
						alt={anImage.alt}
						className="mnDetailFigureImage"
						loading={images.length > 1 ? 'lazy' : undefined}
					/>
					{anImage.caption !== undefined && (
						<figcaption className="mnDetailFigureCaption">{anImage.caption}</figcaption>
					)}
				</figure>
			))}
		</View>
	)

	if (isPlate(aBand) && aBand.images !== undefined) {
		const plate = aBand.images[0]
		return (
			<View id={aBand.id} className="mnBand mnDetailBand mnDetailPlate">
				<figure className="mnDetailFigure">
					<img src={plate.src} alt={plate.alt} className="mnDetailFigureImage" />
					<figcaption className="mnDetailFigureCaption">{aBand.kicker}</figcaption>
				</figure>
			</View>
		)
	}

	return (
		<View id={aBand.id} className="mnBand mnDetailBand">
			<View className="mnDetailBandHead">
				<span className="mnKicker">{aBand.kicker}</span>
				{aBand.link !== undefined && (
					<a className="mnTextLink" href={aBand.link.to} onClick={go(aBand.link.to)}>
						{aBand.link.label}
					</a>
				)}
			</View>

			<View className={`mnDetailBandBody ${isSplit(aBand) ? 'mnDetailBandSplit' : ''}`}>
				<View className="mnDetailBandText">
					{aBand.lede !== undefined && <p className="mnDetailLede">{aBand.lede}</p>}

					{aBand.rows !== undefined && (
						<View className="mnSpecTable">
							{aBand.rows.map(aRow => (
								<View key={aRow.label} className="mnSpecRow">
									<span className="mnSpecLabel">{aRow.label}</span>
									<span className="mnSpecValue">{aRow.value}</span>
								</View>
							))}
						</View>
					)}

					{aBand.note !== undefined && <p className="mnDetailNote">{aBand.note}</p>}
				</View>

				{aBand.images !== undefined && renderImages(aBand.images)}
			</View>
		</View>
	)
}

/**
 * 밴드 스타일 — 페이지 css() 안에서 `...monoBandStyle` 로 스프레드한다.
 * 객체 리터럴이라 같은 키를 페이지에서 다시 쓰면 통째로 덮인다 (DESIGN_SYSTEM §10).
 */
export const monoBandStyle = {
	'.mnDetailBand': {
		// monoShared 의 `.mnBand` 값을 같이 다시 적는다 — 같은 키는 뒤가 이긴다 (§10 함정)
		borderTop: `1px solid ${mono.line}`,
		padding: 'clamp(56px, 6.6vw, 90px) 0',
		// 서브 네비(72 + 56) 아래로 착지시킨다
		scrollMarginTop: 128,
	},

	/** 섹션 헤더 — 키커(좌) + 이 섹션에서 나가는 링크(우) */
	'.mnDetailBandHead': {
		display: 'flex',
		alignItems: 'baseline',
		justifyContent: 'space-between',
		gap: 24,
		paddingBottom: 20,
		marginBottom: 'clamp(32px, 3.4vw, 48px)',
		borderBottom: `1px solid ${mono.line}`,
	},

	/**
	 * 사진 한 장이 밴드를 채우는 자리. 키커를 사진 위에 띄우면 큰 화면에서 작은 라벨 하나가
	 * 전폭 규칙선 위에 떠 있고 그 아래에 슬래브가 놓인 꼴이 된다. 라벨을 사진 아래 캡션으로
	 * 내리면 사진이 밴드를 그대로 쓰고, 설명이 아니라 주석이 된다 (PERSONA §1).
	 */
	'.mnDetailPlate': {
		'.mnDetailFigure': {
			margin: 0,
			width: '100%',

			'.mnDetailFigureImage': {
				display: 'block',
				width: '100%',
				border: `1px solid ${mono.line}`,
				background: mono.surface,
			},
			'.mnDetailFigureCaption': {
				display: 'block',
				marginTop: 14,
				fontSize: 12,
				letterSpacing: '.3em',
				textTransform: 'uppercase' as const,
				color: mono.faint,
				fontWeight: 500,
			},
		},
	},

	'.mnDetailBandBody': {
		display: 'flex',
		flexDirection: 'column' as const,
		alignItems: 'flex-start',
		gap: 'clamp(32px, 3.4vw, 48px)',

		'.mnDetailBandText': {
			width: '100%',
			display: 'flex',
			flexDirection: 'column' as const,
			alignItems: 'flex-start',
			gap: 'clamp(28px, 2.8vw, 40px)',
		},

		/**
		 * 글 7 : 사진 5. 사진은 **글 높이만큼 늘어난다** — 자기 비율대로 두면 표가 긴 밴드에서
		 * 오른쪽 아래에 공백이 남는다.
		 */
		'&.mnDetailBandSplit': {
			display: 'grid',
			gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 5fr)',
			gap: 'clamp(32px, 4vw, 64px)',
			alignItems: 'stretch',

			'.mnDetailFigures': {
				height: '100%',

				'.mnDetailFigure': {
					height: '100%',

					'.mnDetailFigureImage': {
						width: '100%',
						height: '100%',
						objectFit: 'cover' as const,
						objectPosition: 'center',
						minHeight: 300,
					},
				},
			},

			/**
			 * 1열로 내려오면 늘리기를 푼다. 위 규칙이 3단이라 여기서도 **같은 깊이로 적어야**
			 * 이긴다 — 미디어 쿼리는 특이성을 더해 주지 않는다.
			 */
			'@media (max-width: 900px)': {
				gridTemplateColumns: '1fr',
				gap: 32,

				'.mnDetailFigures': {
					height: 'auto',

					'.mnDetailFigure': {
						height: 'auto',

						'.mnDetailFigureImage': {
							height: 'auto',
							minHeight: 0,
							objectFit: 'contain' as const,
						},
					},
				},
			},
		},
	},

	/** 리드는 읽기 폭에서 끊는다 — 전폭으로 늘리지 않는다 */
	'.mnDetailLede': {
		fontSize: 'clamp(22px, 2.1vw, 30px)',
		lineHeight: 1.4,
		fontWeight: 200,
		color: mono.ink,
		maxWidth: 900,
	},

	'.mnDetailNote': {
		fontSize: 16,
		lineHeight: 1.7,
		fontWeight: 300,
		color: mono.dim,
		maxWidth: 720,
	},

	/** 스펙 테이블 — 헤어라인은 전폭으로 긋고, 값은 읽기 폭에서 끊는다 */
	'.mnSpecTable': {
		width: '100%',

		'.mnSpecRow': {
			display: 'grid',
			gridTemplateColumns: 'minmax(0, 280px) minmax(0, 1fr)',
			gap: 'clamp(24px, 3vw, 56px)',
			padding: '18px 0',
			borderBottom: `1px solid ${mono.line}`,
			alignItems: 'baseline',

			'.mnSpecLabel': {
				fontSize: 13,
				letterSpacing: '.14em',
				textTransform: 'uppercase' as const,
				color: mono.faint,
				fontWeight: 500,
			},
			'.mnSpecValue': {
				fontSize: 17,
				lineHeight: 1.55,
				fontWeight: 300,
				color: mono.ink,
				maxWidth: 780,
			},

			'@media (max-width: 700px)': {
				gridTemplateColumns: '1fr',
				gap: 6,
			},
		},
	},

	'.mnDetailFigures': {
		width: '100%',

		/** 순서가 있는 묶음(설치 절차)은 3열. 900px 2열, 600px 1열 */
		'&.mnDetailFigureGrid': {
			display: 'grid',
			gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
			gap: 'clamp(20px, 2.2vw, 32px)',

			// 절차 사진은 칸을 채운다 — 번호가 붙는 격자라 크기가 들쭉날쭉하면 순서가 안 읽힌다
			'.mnDetailFigureImage': { width: '100%' },

			'@media (max-width: 900px)': {
				gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
			},
			'@media (max-width: 600px)': {
				gridTemplateColumns: '1fr',
			},
		},

		'.mnDetailFigure': {
			margin: 0,
			width: '100%',

			/**
			 * 래스터는 **원본 폭을 넘겨 늘리지 않는다**. `width: 100%` 로 두면 800px 짜리 UI 캡처가
			 * 1296 으로 확대돼 뭉갠다. auto + max-width 면 원본보다 크면 줄고, 작으면 제자리에 선다.
			 */
			'.mnDetailFigureImage': {
				display: 'block',
				width: 'auto',
				maxWidth: '100%',
				border: `1px solid ${mono.line}`,
				background: mono.surface,
			},
			'.mnDetailFigureCaption': {
				display: 'block',
				marginTop: 12,
				fontSize: 13,
				lineHeight: 1.6,
				fontWeight: 300,
				color: mono.faint,
			},
		},
	},
}
